import { useColumnFilter } from "@/hooks/use-column-filter";
import { ColumnFilterPopover, type SortDirection } from "@/components/base/column-filter-popover";
import { ColumnHeader } from "@/components/base/column-header";

type ColumnHeaderOptions = {
    options?: Array<{ label: string; value: string }>;
    hint?: string;
    filterable?: boolean;
    sortable?: boolean;
};

export function usePolicyTableColumnHeader<TColumnKey extends string>(
    storageKey: string,
    activeFilters: Partial<Record<TColumnKey, string>>,
    sort: { by: TColumnKey; order: SortDirection } | null,
    onFilterApply: (column: TColumnKey, value: string) => void,
    onFilterClear: (column: TColumnKey) => void,
    onSortChange: (column: TColumnKey) => void,
) {
    const { activeFilterColumn, activeFilterAnchor, filterInput, searchHistory, setFilterInput, openColumnFilter, closeFilter, removeSaved, applyFilter } =
        useColumnFilter<TColumnKey>(storageKey);

    const colHeader = (key: TColumnKey, label: string, opts?: ColumnHeaderOptions) => {
        const isFilterable = opts?.filterable !== false;
        const isSortable = opts?.sortable !== false;
        const isCurrentSort = sort?.by === key;
        const currentDirection = isCurrentSort ? sort?.order : undefined;
        const inputIsReady = Boolean(filterInput.trim());

        const handleApply =
            isFilterable && inputIsReady
                ? () => {
                      applyFilter(filterInput, !opts?.options);
                      onFilterApply(key, filterInput.trim());
                  }
                : undefined;

        const handleClear =
            isFilterable && activeFilters[key]
                ? () => {
                      onFilterClear(key);
                      closeFilter();
                  }
                : undefined;

        return [
            <ColumnHeader
                key={`${key}-header`}
                label={label}
                isFilterable={isFilterable}
                isFilterActive={Boolean(activeFilters[key])}
                isFilterOpen={activeFilterColumn === key}
                onFilterPress={(anchorEl) => openColumnFilter(key, anchorEl, activeFilters[key] ?? "")}
                isSortable={isSortable}
                isSortActive={isCurrentSort}
                sortDirection={currentDirection}
                onSort={() => onSortChange(key)}
            />,
            isFilterable ? (
                <ColumnFilterPopover
                    key={`${key}-filter`}
                    isOpen={activeFilterColumn === key}
                    anchorEl={activeFilterAnchor}
                    columnLabel={label}
                    hint={opts?.hint}
                    options={opts?.options}
                    searchValue={filterInput}
                    savedValues={opts?.options ? undefined : (searchHistory[key] ?? [])}
                    onSearchChange={setFilterInput}
                    onClose={closeFilter}
                    onApply={handleApply}
                    onClear={handleClear}
                    onRemoveSavedValue={removeSaved}
                    onSelectValue={
                        isFilterable
                            ? (value) => {
                                  applyFilter(value, false);
                                  onFilterApply(key, value);
                              }
                            : undefined
                    }
                />
            ) : null,
        ];
    };

    return colHeader;
}
